/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  SchedulationResult,
  ServiceProviderAuditRecordDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce un record di audit del service provider per la tracciatura AGID secondo il comma 2 dell’articolo 13 del DPCM
 * @summary Restituisce un record di audit del service provider per la tracciatura AGID secondo il comma 2 dell’articolo 13 del DPCM
 */
export const getServiceProviderAuditId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ServiceProviderAuditRecordDTO>(
    {
      url: `/framework-cfg-utenti/service-provider-audit/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetServiceProviderAuditIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/service-provider-audit/${id}`] as const;

export const getGetServiceProviderAuditIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getServiceProviderAuditId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getServiceProviderAuditId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getServiceProviderAuditId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetServiceProviderAuditIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getServiceProviderAuditId>>
  > = ({ signal }) => getServiceProviderAuditId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetServiceProviderAuditIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getServiceProviderAuditId>>
>;
export type GetServiceProviderAuditIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce un record di audit del service provider per la tracciatura AGID secondo il comma 2 dell’articolo 13 del DPCM
 */
export const useGetServiceProviderAuditId = <
  TData = Awaited<ReturnType<typeof getServiceProviderAuditId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getServiceProviderAuditId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetServiceProviderAuditIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Chiamata batch che uno scheduler può effettuare per eliminare i record di Audit Service Provider presenti da più di {maxNumMonths} mesi
 * @summary Chiamata batch che uno scheduler può effettuare per eliminare i record di Audit Service Provider presenti da più di {maxNumMonths} mesi
 */
export const deleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonths = (
  maxNumMonths: number,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<SchedulationResult>(
    {
      url: `/framework-cfg-utenti/service-provider-audit/remove-elapsed-audit-record/${maxNumMonths}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonthsMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof deleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonths
        >
      >,
      TError,
      { maxNumMonths: number },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof deleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonths
      >
    >,
    TError,
    { maxNumMonths: number },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof deleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonths
        >
      >,
      { maxNumMonths: number }
    > = props => {
      const { maxNumMonths } = props ?? {};

      return deleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonths(
        maxNumMonths,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type DeleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonthsMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof deleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonths
      >
    >
  >;

export type DeleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonthsMutationError =
  ErrorType<unknown>;

/**
 * @summary Chiamata batch che uno scheduler può effettuare per eliminare i record di Audit Service Provider presenti da più di {maxNumMonths} mesi
 */
export const useDeleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonths =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof deleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonths
        >
      >,
      TError,
      { maxNumMonths: number },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }) => {
    const mutationOptions =
      getDeleteServiceProviderAuditRemoveElapsedAuditRecordMaxNumMonthsMutationOptions(
        options,
      );

    return useMutation(mutationOptions);
  };
